/**
 * Page de retour après Stripe Checkout
 *
 * Stripe Checkout Session redirige ici après paiement:
 * - /checkout?status=success → Sync Stripe + Confirmation + redirection vers Dashboard
 * - /checkout?status=cancel  → Message + retour vers Pricing
 *
 * IMPORTANT: On appelle syncSubscription() au chargement pour s'assurer que le statut
 * est bien à jour en BDD, même si le webhook n'est pas encore arrivé.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, XCircle, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { subscriptionsAPI } from '@/utils/api';

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const status = searchParams.get('status');

  // On success, sync subscription from Stripe to ensure DB is up to date
  useEffect(() => {
    if (status === 'success' && !syncDone) {
      setSyncing(true);
      subscriptionsAPI.sync()
        .catch(() => {
          // Webhook may handle it — non-blocking
        })
        .finally(() => {
          setSyncing(false);
          setSyncDone(true);
        });
    }
  }, [status, syncDone]);

  // Success: subscription activated via Stripe Checkout
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-10 shadow-xl shadow-gray-300/10 border border-white/30">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-100/50 backdrop-blur-xl rounded-full mb-6">
              {syncing ? (
                <Loader2 className="h-10 w-10 text-teal-600 animate-spin" />
              ) : (
                <Check className="h-10 w-10 text-teal-600" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {syncing ? 'Activation en cours...' : 'Paiement confirmé !'}
            </h2>
            <p className="text-gray-600 text-lg mb-2">
              {syncing
                ? 'Nous synchronisons votre abonnement...'
                : 'Votre abonnement a été activé avec succès.'}
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Vous pouvez maintenant profiter de toutes les fonctionnalités de votre plan.
            </p>
            <button
              onClick={() => navigate('/billing')}
              disabled={syncing}
              className="w-full py-4 px-6 rounded-2xl font-semibold text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/30 hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              Accéder au Dashboard
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Cancel: user abandoned Stripe Checkout
  if (status === 'cancel') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-10 shadow-xl shadow-gray-300/10 border border-white/30">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100/50 backdrop-blur-xl rounded-full mb-6">
              <XCircle className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Paiement annulé
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Aucun prélèvement n'a été effectué. Vous pouvez réessayer à tout moment.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full py-4 px-6 rounded-2xl font-semibold text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/30 hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              Retour aux plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No status parameter: redirect to pricing
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-10 shadow-xl shadow-gray-300/10 border border-white/30">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Page de checkout
          </h2>
          <p className="text-gray-600 mb-8">
            Veuillez sélectionner un plan depuis la page de tarification.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/30 text-white rounded-2xl font-semibold transition-all duration-300"
          >
            Voir les plans
          </button>
        </div>
      </div>
    </div>
  );
}
